import { useEffect, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { ChevronDown } from "lucide-react";
import { useSessionQuery } from "@/lib/queries";
import { useAuth } from "@/lib/auth";
import { useSession } from "@/lib/session";
import { MySettings } from "@/components/settings/MySettings";
import type { Role } from "@/types";
import { useTheme } from "@/themes/useTheme";
import { GUTTER } from "@/components/layout/Page";

interface Tab {
  to: string;
  label: string;
  /** The lowest role that may see this tab. Absent means everyone. */
  minRole?: Role;
}

// ERIKA's navigation had eight entries with role checks spelled inline as
// `getRole.data.role[0] !== "admin"` and, in one case, a hard-coded email
// address. The same gating, as data, so adding a page does not mean writing
// another expression.
const TABS: Tab[] = [
  { to: "/ask", label: "Ask" },
  { to: "/library", label: "Library" },
  { to: "/questions", label: "Questions" },
  // A manager curates the vocabulary; that is the librarian's job.
  { to: "/admin/tags", label: "Tags", minRole: "manager" },
  { to: "/admin/users", label: "Users", minRole: "admin" },
  { to: "/admin/analytics", label: "Analytics", minRole: "admin" },
];

const RANK: Record<Role, number> = { member: 0, manager: 1, admin: 2, owner: 3 };

function permitted(tab: Tab, role: Role | undefined): boolean {
  if (!tab.minRole) return true;
  // Until the session lands, the admin tabs stay hidden rather than appearing
  // and vanishing — a nav that reflows on every page load is worse than one
  // that fills in a beat late.
  if (!role) return false;
  return RANK[role] >= RANK[tab.minRole];
}

/**
 * The application bar.
 *
 * Full width, fixed height, and it does not scroll — it is the one piece of
 * furniture outside the routes. The tabs sit beside the wordmark rather than
 * centred in the bar: centred is fine at 1152px and turns into a row of links
 * marooned in the middle of an empty 2560px strip, with the wordmark and the
 * account menu a foot away at either end.
 *
 * `shrink-0` matters. Without it the flex column above squeezes the bar
 * instead of the route when a page is too tall, and the nav loses a pixel of
 * height per overflowing row.
 */
export function TopNav() {
  const session = useSessionQuery();
  const auth = useAuth();
  const { switchOrg } = useSession();
  const role = session.data?.user.role;
  const memberships = session.data?.memberships ?? [];
  // The staff console is Two Dogs' and is never offered to a customer; the
  // server answers 404 there to anyone else regardless.
  const tabs = [
    ...TABS.filter((t) => permitted(t, role)),
    ...(session.data?.user.staff ? [{ to: "/staff/extraction", label: "Pipeline" }] : []),
  ];
  const [settingsOpen, setSettingsOpen] = useState(false);
  const theme = useTheme();

  return (
    <header className="relative z-40 shrink-0 border-b border-[rgb(var(--c-nav-rule))]">
      <div className={`flex h-14 items-center gap-2 sm:gap-4 ${GUTTER}`}>
        <Link
          to="/ask"
          className="erika-serif shrink-0 rounded text-xl tracking-wide text-chrome focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome/40"
        >
          {theme.logo ? (
            <img src={theme.logo} alt={theme.productName} className="h-10 w-auto" />
          ) : (
            theme.productName
          )}
        </Link>

        {/* A hairline between the wordmark and the tabs, so the two groups do
            not read as one run of links. */}
        <span aria-hidden className="hidden h-5 w-px shrink-0 bg-chrome/15 sm:block" />

        <nav className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
          {tabs.map((tab) => (
            <Link
              key={tab.to}
              to={tab.to}
              className="shrink-0 rounded-md px-3 py-1.5 text-sm font-medium text-chrome/75 transition-colors duration-100 ease-snap hover:bg-chrome/10 hover:text-chrome focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome/40"
              activeProps={{ className: "!bg-accent !text-accent-foreground" }}
              activeOptions={{ exact: false }}
            >
              {tab.label}
            </Link>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2">
          {auth.isDev && (
            <span
              className="rounded bg-amber-400/20 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-200"
              title="APP_DEV_SUBJECT is set: this server is not verifying tokens"
            >
              dev auth
            </span>
          )}
          {/* Which org this tab is in is worth stating — it is the difference
              between two tabs open on two customers' libraries. A person in
              more than one gets a switcher; a person in one gets the name and
              nothing to click. Beside the account menu, because both answer
              "who am I, and where". */}
          {session.data && memberships.length > 1 && (
            <select
              aria-label="Organisation"
              value={session.data.org.id}
              onChange={(e) => {
                const target = memberships.find((m) => m.orgId === e.target.value);
                if (target) switchOrg(target);
              }}
              className="max-w-[180px] rounded-md border border-chrome/20 bg-[rgb(var(--c-chrome-surface))] px-2 py-1 text-xs text-chrome/85"
            >
              {memberships.map((m) => (
                <option key={m.orgId} value={m.orgId}>
                  {/* An org that needs its own provider says so, because
                      choosing it goes through that provider's sign-in. */}
                  {m.usable ? m.orgName : `${m.orgName} (sign in with SSO)`}
                </option>
              ))}
            </select>
          )}
          {session.data && memberships.length <= 1 && session.data.deployment.mode === "saas" && (
            <span className="hidden max-w-[180px] truncate text-xs text-chrome/60 lg:inline">
              {session.data.org.name || session.data.org.slug}
            </span>
          )}
          {/* One menu rather than an address and two naked links. The address
              was the widest thing on the right-hand side and the least
              clickable, and "Sign out" sitting permanently next to it is a
              destructive action one stray click away. */}
          <AccountMenu
            email={session.data?.user.email}
            role={session.data?.user.role}
            onSettings={() => setSettingsOpen(true)}
            onSignOut={auth.signOut}
          />
        </div>
      </div>
      {settingsOpen && <MySettings onClose={() => setSettingsOpen(false)} />}
    </header>
  );
}

/** The initials shown in the avatar, from whatever part of the address we have. */
function initials(email: string | undefined): string {
  const local = email?.split("@")[0] ?? "";
  if (!local) return "?";
  const [first, second] = local.split(/[._-]+/).filter(Boolean);
  if (first && second) return `${first.slice(0, 1)}${second.slice(0, 1)}`.toUpperCase();
  return local.slice(0, 2).toUpperCase();
}

function AccountMenu({
  email,
  role,
  onSettings,
  onSignOut,
}: {
  email: string | undefined;
  role: Role | undefined;
  onSettings: () => void;
  onSignOut: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Click-away and Escape, the same contract as the filter popovers.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        className="flex items-center gap-1.5 rounded-full py-1 pl-1 pr-1.5 text-chrome/75 transition-colors duration-100 ease-snap hover:bg-chrome/10 hover:text-chrome focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chrome/40"
      >
        <span className="grid h-7 w-7 place-items-center rounded-full bg-chrome/15 text-[11px] font-medium text-chrome">
          {initials(email)}
        </span>
        <ChevronDown size={14} aria-hidden className={open ? "rotate-180" : undefined} />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 z-50 mt-1.5 w-60 overflow-hidden rounded-card border border-border bg-card shadow-panel"
        >
          <div className="border-b border-border px-3 py-2.5">
            <p className="truncate text-xs font-medium" title={email}>
              {email ?? "Signed in"}
            </p>
            {role && <p className="mt-0.5 text-xs capitalize text-muted-foreground">{role}</p>}
          </div>
          <button
            type="button"
            role="menuitem"
            // My Settings lives on the nav rather than on the Ask page because
            // what it sets — whether Advanced Options starts open — has to be
            // readable before that page has decided how to render.
            onClick={() => {
              setOpen(false);
              onSettings();
            }}
            className="block w-full px-3 py-2 text-left text-xs transition-colors duration-100 ease-snap hover:bg-muted"
          >
            My settings
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={onSignOut}
            className="block w-full border-t border-border px-3 py-2 text-left text-xs text-muted-foreground transition-colors duration-100 ease-snap hover:bg-muted hover:text-foreground"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
